import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Aperture, Zap, BookOpen, Search } from 'lucide-react';
import { PageHero } from '@/components/PageHero';
import { CTASection } from '@/components/CTASection';
import { Reveal } from '@/components/Reveal';
import { SEO } from '@/components/SEO';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { SkeletonList } from '@/components/SkeletonList';
import { getGearLibrary } from '@/lib/api';
import { SITE_IMAGES } from '@/lib/siteImages';
import { GEAR_LIBRARY_PAGE } from '@/constants/testIds';

const TYPE_META = {
  camera: { label: 'Cameras', icon: Camera },
  lens: { label: 'Lenses', icon: Aperture },
  lighting: { label: 'Lighting', icon: Zap },
  article: { label: 'Technique', icon: BookOpen },
};

function GearCard({ entry }) {
  const Icon = TYPE_META[entry.type]?.icon || Camera;
  return (
    <Link to={`/gear/${entry.slug}`} data-testid={GEAR_LIBRARY_PAGE.card(entry.slug)} className="group block h-full">
      <div className="h-full rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-6 transition-colors duration-200 group-hover:border-[hsl(var(--lxra-gold))]/40">
        <div className="flex items-center justify-between mb-4">
          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[hsl(var(--lxra-gold))]/35 text-[hsl(var(--lxra-gold))]">
            <Icon className="h-4 w-4" />
          </span>
          {entry.brand && <span className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))]">{entry.brand}</span>}
        </div>
        <h3 className="font-display text-lg text-[hsl(var(--lxra-fg))] group-hover:text-[hsl(var(--lxra-gold))] transition-colors">{entry.title}</h3>
        <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed line-clamp-3">{entry.summary}</p>
        {entry.good_for?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {entry.good_for.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="secondary" className="font-normal text-[10px]">{tag}</Badge>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function GearLibrary() {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(false);
  const [type, setType] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    getGearLibrary()
      .then(setEntries)
      .catch(() => setError(true));
  }, []);

  const filtered = useMemo(() => {
    if (!entries) return [];
    return entries.filter((e) => {
      if (type !== 'all' && e.type !== type) return false;
      if (q.trim()) {
        const needle = q.trim().toLowerCase();
        return e.title.toLowerCase().includes(needle) || e.brand.toLowerCase().includes(needle) || e.summary.toLowerCase().includes(needle);
      }
      return true;
    });
  }, [entries, type, q]);

  return (
    <div data-testid={GEAR_LIBRARY_PAGE.root}>
      <SEO
        title="Gear Guide"
        description="A reference library of camera bodies, lenses, and lighting equipment — plus technique guides — curated by LXRA Studios."
      />
      <PageHero
        kicker="Reference Library"
        title="GEAR GUIDE"
        subtitle="Camera bodies, lenses, lighting, and technique — a growing reference library for photographers and curious clients alike."
        backgroundImage={SITE_IMAGES.aboutHero || SITE_IMAGES.galleriesHero}
      />

      <section className="bg-[#0A0A0A]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
            <ToggleGroup type="single" value={type} onValueChange={(v) => v && setType(v)} className="inline-flex flex-wrap rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] p-1">
              {['all', 'camera', 'lens', 'lighting', 'article'].map((t) => (
                <ToggleGroupItem
                  key={t}
                  value={t}
                  data-testid={GEAR_LIBRARY_PAGE.typeFilter(t)}
                  className="px-3.5 py-1.5 rounded-lg text-xs uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))] data-[state=on]:bg-[hsl(var(--lxra-gold))] data-[state=on]:text-[#0A0A0A]"
                >
                  {t === 'all' ? 'All' : TYPE_META[t].label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--lxra-muted))]" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search gear..."
                data-testid={GEAR_LIBRARY_PAGE.searchInput}
                className="pl-9 bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10 focus-visible:ring-[hsl(var(--lxra-gold))]"
              />
            </div>
          </div>

          {error && <p className="text-sm text-[hsl(var(--lxra-muted))]">Unable to load the gear guide right now. Please try again shortly.</p>}

          {!entries && !error && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={GEAR_LIBRARY_PAGE.grid}>
              <SkeletonList count={6} className="h-48 rounded-2xl bg-[#141416]" />
            </div>
          )}

          {entries && filtered.length === 0 && (
            <p data-testid={GEAR_LIBRARY_PAGE.emptyState} className="text-sm text-[hsl(var(--lxra-muted))]">
              No entries match yet — try a different filter or search term.
            </p>
          )}

          {entries && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={GEAR_LIBRARY_PAGE.grid}>
              {filtered.map((entry, i) => (
                <Reveal key={entry.slug} delay={i * 0.04}>
                  <GearCard entry={entry} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTASection testId="gear-library-cta-banner" buttonTestId="gear-library-cta-book-now-button" />
    </div>
  );
}
